import { AxiosResponse } from 'axios';

import { api } from '@/lib/api-client.ts';

import { EnrichedDCConfig } from '@/models/EnrichedPrismicDocuments.ts';
import { PVSProvider } from '@/models/customers/PVSProvider.ts';
import { DataCurPracUser } from '@/models/data-cur/DataCurPracUser.ts';
import { CreateDCTaskDto, DCTask, EnrichedTask, UpdateDCTaskDto } from '@/models/doc-cirrus/DCTask.ts';
import { DCTaskTypeInternal } from '@/models/doc-cirrus/DCTaskTypes.ts';
import { DCUser } from '@/models/doc-cirrus/DCUser.ts';

import { ServiceUrl } from '@/services/ServiceURL.ts';

import { getCustomerId } from '@/utils/customer-utils.ts';
import { isDevEnvironment } from '@/utils/envUtils.ts';
import { enrichTask } from '@/utils/task-utils.ts';

interface GetTasksResponse {
    data: {
        tasks: DCTask[];
        task_types: DCTaskTypeInternal[];
    };
    meta: {
        count: number;
        items_per_page?: number;
        page?: number;
        total_items?: number;
        total_pages?: number;
    };
}

export const getTasks = async (
    instance_id: string,
    include_open: boolean = false,
    include_done: boolean = false
): Promise<[DCTask[], DCTaskTypeInternal[]]> => {
    // First, get the first page to determine total pages
    const first_page_result = await getTasksWithPagination(instance_id, include_open, include_done, 0);

    if (!first_page_result) {
        return [[], []];
    }

    const [first_data, first_meta] = first_page_result;
    const total_pages = first_meta.total_pages ?? 1;

    // If there's only one page, return the first page data
    if (total_pages <= 1) {
        return [first_data.tasks, first_data.task_types];
    }

    // Create promises for all remaining pages (pages 1 to total_pages-1)
    const remaining_page_promises = Array.from({ length: total_pages - 1 }, (_, index) =>
        getTasksWithPagination(instance_id, include_open, include_done, index + 1)
    );

    // Fetch all remaining pages in parallel
    const settled_results = await Promise.allSettled(remaining_page_promises);

    // Start with first page data
    const tasks: DCTask[] = [...first_data.tasks];
    const task_types: DCTaskTypeInternal[] = [...first_data.task_types]; // Keep only from first page

    // Process remaining pages - only collect tasks, task_types are the same across pages
    for (const result of settled_results) {
        if (result.status === 'fulfilled' && result.value) {
            const [data] = result.value;
            tasks.push(...data.tasks);
        }
    }

    return [tasks, task_types];
};

export const getTasksWithPagination = async (
    instance_id: string,
    include_open: boolean = false,
    include_done: boolean = false,
    page: number = 0
): Promise<[GetTasksResponse['data'], GetTasksResponse['meta']] | undefined> => {
    const size: number | undefined = isDevEnvironment() ? 50 : undefined; // testing on dev

    try {
        const response: AxiosResponse<GetTasksResponse> = await api.get(
            ServiceUrl.PATIENT_API_DOMAIN.SECURE_ADMIN.DOC_CIRRUS.TASKS,
            {
                headers: {
                    customer_id: getCustomerId(),
                },
                params: {
                    instance_id,
                    include_done,
                    include_open,
                    page,
                    size,
                },
            }
        );

        return [response.data?.data, response.data?.meta];
    } catch (error) {
        throw new Error(`Error fetching tasks: ${String(error)}`);
    }
};

export const getTask = async (instance_id?: string, task_id?: string): Promise<DCTask | undefined> => {
    if (!instance_id || !task_id) {
        return undefined;
    }

    try {
        const response: AxiosResponse<GetTasksResponse> = await api.get(
            ServiceUrl.PATIENT_API_DOMAIN.SECURE_ADMIN.DOC_CIRRUS.TASKS,
            {
                headers: {
                    customer_id: getCustomerId(),
                },
                params: {
                    instance_id: instance_id,
                    dc_task_id: task_id,
                },
            }
        );

        const task = response.data.data?.tasks[0];

        if (!task) {
            throw new Error(`Task not found`);
        }

        return task;
    } catch (error) {
        throw new Error(`Error fetching task: ${String(error)}`);
    }
};

export const getOpenTasksWithTypesFromMultipleInstances = async (
    configs: EnrichedDCConfig[]
): Promise<[DCTask[], DCTaskTypeInternal[]]> => {
    const [tasks, task_types] = await getTasksFromMultipleInstances(configs, true, false, true);
    return [tasks, task_types];
};

export const getDoneTasksFromMultipleInstances = async (configs: EnrichedDCConfig[]): Promise<DCTask[]> => {
    const [tasks] = await getTasksFromMultipleInstances(configs, false, true, false);
    return tasks;
};

const getTasksFromMultipleInstances = async (
    configs: EnrichedDCConfig[],
    include_open: boolean = false,
    include_done: boolean = false,
    include_task_types: boolean = false
): Promise<[DCTask[], DCTaskTypeInternal[]]> => {
    // Filter to only Doc Cirrus providers
    const doc_cirrus_configs = configs.filter(({ pvs_provider }) => pvs_provider === PVSProvider.DOC_CIRRUS);

    if (doc_cirrus_configs.length === 0) {
        return [[], []];
    }

    const settled_promises = await Promise.allSettled(
        doc_cirrus_configs.map(({ instance_id }) => getTasks(instance_id, include_open, include_done))
    );

    // Extract and filter tasks from successful responses
    const tasks: DCTask[] = [];
    const task_types: DCTaskTypeInternal[] = [];

    for (const promise of settled_promises) {
        if (promise.status === 'fulfilled' && promise.value) {
            const [task_data, type_data] = promise.value;

            // Filter and collect tasks
            const up_to_date_tasks = task_data.filter((task) => task.sync_state === 'UPTODATE');
            tasks.push(...up_to_date_tasks);

            // Collect task types if requested
            if (include_task_types) {
                task_types.push(...type_data);
            }
        }
    }

    return [tasks, task_types];
};

const createTask = async (instance_id: string, task_data: CreateDCTaskDto): Promise<DCTask> => {
    try {
        const response: AxiosResponse<{ data: DCTask }> = await api.post(
            `${ServiceUrl.PATIENT_API_DOMAIN.SECURE_ADMIN.DOC_CIRRUS.TASKS}/create`,
            task_data,
            {
                headers: {
                    customer_id: getCustomerId(),
                },
                params: {
                    instance_id: instance_id,
                },
            }
        );

        return response.data.data;
    } catch (error) {
        throw new Error(`Error creating task: ${String(error)}`);
    }
};

export const createTaskAndEnrich = async (
    instance_id: string,
    task_data: CreateDCTaskDto,
    task_types: DCTaskTypeInternal[],
    instance_users_map: Record<string, DCUser | DataCurPracUser> | undefined
): Promise<EnrichedTask> => {
    const task = await createTask(instance_id, task_data);
    return enrichTask(task, task_types, instance_users_map);
};

const updateTask = async (instance_id: string, task_id: string, task_data: UpdateDCTaskDto): Promise<DCTask> => {
    try {
        const response: AxiosResponse<{ data: DCTask }> = await api.post(
            `${ServiceUrl.PATIENT_API_DOMAIN.SECURE_ADMIN.DOC_CIRRUS.TASKS}/update`,
            task_data,
            {
                headers: {
                    customer_id: getCustomerId(),
                },
                params: {
                    instance_id: instance_id,
                    dc_task_id: task_id,
                },
            }
        );

        return response.data.data;
    } catch (error) {
        throw new Error(`Error updating task: ${String(error)}`);
    }
};

export const updateTaskAndEnrich = async (
    instance_id: string,
    task_id: string,
    task_data: UpdateDCTaskDto,
    task_types: DCTaskTypeInternal[],
    instance_users_map: Record<string, DCUser | DataCurPracUser> | undefined
): Promise<EnrichedTask> => {
    const task = await updateTask(instance_id, task_id, task_data);
    return enrichTask(task, task_types, instance_users_map);
};

export const cancelTask = async (instance_id: string, task_id: string): Promise<void> => {
    try {
        await api.post(
            `${ServiceUrl.PATIENT_API_DOMAIN.SECURE_ADMIN.DOC_CIRRUS.TASKS}/cancel`,
            {},
            {
                headers: {
                    customer_id: getCustomerId(),
                },
                params: {
                    instance_id: instance_id,
                    dc_task_id: task_id,
                },
            }
        );
    } catch (error) {
        throw new Error(`Error canceling task: ${String(error)}`);
    }
};
